<template>
  <div class="sidebar-inner d-flex flex-column h-100">
    <!-- Header / Brand Logo -->
    <div class="sidebar-brand d-flex align-items-center justify-content-between">
      <div class="brand-logo" v-if="!colapsado || isMobile" style="cursor: pointer;" @click="navTo('/home')">
        <img src="https://pub-3f3d3f8847a94e1e89883126e9a8db4c.r2.dev/config/logo.png" alt="Servease Logo" />
      </div>
      <div class="brand-logo-collapsed" v-else>
        <!-- Icon only when collapsed on desktop -->
        <strong>S</strong>
      </div>

      <!-- Hamburger toggle on Desktop inside sidebar -->
      <button class="toggle-btn d-none d-md-flex" @click="$emit('toggle-menu')" aria-label="Toggle Sidebar">
        <i :class="colapsado ? 'bi bi-list' : 'bi bi-text-indent-left'"></i>
      </button>

      <!-- Close btn on mobile -->
      <button class="close-mobile-btn d-flex d-md-none" @click="$emit('close-mobile')">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <!-- Active User Profile Card -->
    <div class="sidebar-user" :class="{ 'collapsed': colapsado && !isMobile }">
      <div class="user-avatar">
        <i class="bi bi-person-circle"></i>
      </div>
      <div class="user-info" v-if="!colapsado || isMobile">
        <h6 class="user-name" :title="username">{{ username }}</h6>
        <span class="user-role">Administrador</span>
      </div>
      <button 
        class="edit-user-btn" 
        v-if="!colapsado || isMobile" 
        @click="navTo('/empresa')" 
        title="Editar perfil"
      >
        <i class="bi bi-gear-fill"></i>
      </button>
    </div>

    <!-- Navigation Menu -->
    <div class="sidebar-nav flex-grow-1">
      <span class="nav-heading" v-if="!colapsado || isMobile">MENU PRINCIPAL</span>
      
      <button 
        class="nav-item" 
        :class="{ active: currentRoute === '/dashboard' }"
        @click="navTo('/dashboard')"
      >
        <i class="bi bi-grid-1x2-fill icon"></i>
        <span class="label" v-if="!colapsado || isMobile">Dashboard</span>
      </button>

      <button 
        class="nav-item" 
        :class="{ active: currentRoute === '/prestadores' }"
        @click="navTo('/prestadores')"
      >
        <i class="bi bi-people-fill icon"></i>
        <span class="label" v-if="!colapsado || isMobile">Prestadores</span>
      </button>

      <button 
        class="nav-item" 
        :class="{ active: currentRoute === '/equipamentos' }"
        @click="navTo('/equipamentos')"
      >
        <i class="bi bi-motherboard-fill icon"></i>
        <span class="label" v-if="!colapsado || isMobile">Equipamentos</span>
      </button>

      <button 
        class="nav-item" 
        :class="{ active: currentRoute === '/servicos' }"
        @click="navTo('/servicos')"
      >
        <i class="bi bi-file-earmark-text-fill icon"></i>
        <span class="label" v-if="!colapsado || isMobile">Serviços</span>
      </button>

      <span class="nav-heading mt-4" v-if="!colapsado || isMobile">INTEGRAÇÕES</span>
      
      <button class="nav-item secondary" @click="abrirShellBox">
        <i class="bi bi-fuel-pump-fill icon text-warning"></i>
        <span class="label" v-if="!colapsado || isMobile">Shell Box</span>
        <i class="bi bi-box-arrow-up-right external-icon" v-if="!colapsado || isMobile"></i>
      </button>

      <button class="nav-item secondary" @click="abrirPortoSeguros">
        <i class="bi bi-shield-fill-check icon text-primary"></i>
        <span class="label" v-if="!colapsado || isMobile">Porto Seguro</span>
        <i class="bi bi-box-arrow-up-right external-icon" v-if="!colapsado || isMobile"></i>
      </button>
    </div>

    <!-- Logout / Footer -->
    <div class="sidebar-footer">
      <button class="logout-btn" @click="irParaLogin" :title="'Sair do sistema'">
        <i class="bi bi-box-arrow-right icon"></i>
        <span class="label" v-if="!colapsado || isMobile">Sair da conta</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from '@/axios.js';
import { showToast } from '@/eventBus.js';

const emit = defineEmits(['toggle-menu', 'close-mobile']);

const props = defineProps({
  colapsado: {
    type: Boolean,
    default: false
  },
  isMobile: {
    type: Boolean,
    default: false
  }
});

const username = ref('');
const router = useRouter();
const route = useRoute();

// Computes current route path to highlight active menu
const currentRoute = computed(() => route.path);

function navTo(path) {
  router.push({ path });
  if (props.isMobile) {
    emit('close-mobile');
  }
}

function abrirPortoSeguros() {
  window.open("https://prestador.portoseguro.com.br/portal/site/pdp/template.LOGIN/", "_blank");
}

function abrirShellBox() {
  window.open("https://portal2.shellboxempresas.com/login?origin=%2Fdashboard", "_blank");
}

function irParaLogin() {
  axios.post('/auth/logout')
    .then(response => {
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      showToast(response.data);
      router.push({ path: '/' });
    })
    .catch(error => {
      showToast(error?.response?.data?.message || 'Erro ao deslogar');
    });
}

onMounted(() => {
  const companyId = localStorage.getItem("idCompany");
  if (!companyId) return;

  username.value = localStorage.getItem('nameUser') || 'Usuário Logado';
  
  // Opcional: Atualizar os dados de nome do usuário pela API
  axios.get(`/api/company/one/${companyId}`)
    .then(response => {
      // do nothing except confirm it works
    }).catch(error => {
      console.error(error);
    });
});
</script>

<style scoped>
/* Base */
.sidebar-inner {
  background-color: #FFFFFF;
  padding: 1.5rem 1rem;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Brand Header */
.sidebar-brand {
  margin-bottom: 2rem;
  height: 40px;
}

.brand-logo img {
  height: 32px;
  object-fit: contain;
}

.brand-logo-collapsed strong {
  font-size: 1.5rem;
  color: #DF523A;
  font-weight: 800;
  display: block;
  text-align: center;
  width: 100%;
}

.toggle-btn, .close-mobile-btn {
  background: transparent;
  border: none;
  font-size: 1.25rem;
  color: #64748B;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-btn:hover, .close-mobile-btn:hover {
  background: #F1F5F9;
  color: #0F172A;
}

/* User Profile Card */
.sidebar-user {
  display: flex;
  align-items: center;
  background: #F8FAFC;
  padding: 0.75rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  gap: 0.75rem;
  border: 1px solid #E2E8F0;
  position: relative;
}

.sidebar-user.collapsed {
  justify-content: center;
  padding: 0.75rem 0;
  border-color: transparent;
  background: transparent;
}

.user-avatar i {
  font-size: 2rem;
  color: #94A3B8;
  line-height: 1;
}

.user-info {
  flex: 1;
  overflow: hidden;
}

.user-name {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: #1E293B;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.user-role {
  font-size: 0.75rem;
  color: #64748B;
  font-weight: 500;
}

.edit-user-btn {
  background: transparent;
  border: none;
  color: #94A3B8;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s;
}

.edit-user-btn:hover {
  color: #DF523A;
}

/* Navigation Menu */
.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.nav-heading {
  font-size: 0.7rem;
  font-weight: 700;
  color: #94A3B8;
  letter-spacing: 0.5px;
  margin-left: 0.75rem;
  margin-bottom: 0.25rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: transparent;
  border: none;
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  color: #475569;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.nav-item .icon {
  font-size: 1.25rem;
  color: #94A3B8;
  transition: color 0.2s;
  width: 24px;
  text-align: center;
}

.nav-item:hover {
  background: #F8FAFC;
  color: #0F172A;
}

.nav-item:hover .icon {
  color: #DF523A;
}

.nav-item.active {
  background: #FFF1F0; /* Soft Coral Background */
  color: #DF523A;
}

.nav-item.active .icon {
  color: #DF523A;
}

.nav-item.secondary {
  font-weight: 500;
}
.nav-item.secondary .external-icon {
  margin-left: auto;
  font-size: 0.8rem;
  color: #94A3B8;
}

/* Collapsed active state alignments */
.collapsed .nav-item {
  justify-content: center;
  padding: 0.875rem 0;
}
.collapsed .nav-item .icon {
  margin: 0;
}

/* Footer / Logout */
.sidebar-footer {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #E2E8F0;
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: transparent;
  border: none;
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  color: #64748B;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.logout-btn .icon {
  font-size: 1.25rem;
}

.logout-btn:hover {
  background: #FEF2F2;
  color: #EF4444; /* Standard Danger Red */
}

.collapsed .logout-btn {
  justify-content: center;
  padding: 0.875rem 0;
}
</style>